import React, { useEffect, useRef, useState } from "react";
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer } from "recharts";
// import GreenTick from "../../images/green-tick.png";
import { useNavigate } from "react-router-dom";
import { getUserPerformance } from "../../api/user";
import PerformanceSection from "./PerformanceSection";
import { performanceEmptyCopy, emptyCurveData, StartDot } from "./performanceEmpty";
import { SkeletonBlock } from "../Skeleton/Skeleton";
import { getLeagueStatus } from "../../pages/Game/landing/leagueStatus";
import { finalizedOnLabel, leagueTypeKey } from "../../pages/Game/landing/leagueFormat";

// Module counter → a unique gradient id per curve instance. Multiple Record
// cards render on a profile; a shared gradient id makes some lose their fill.
let curveIdCounter = 0;

/**
 * "The Record" card on the public profile (route /profile/:userId).
 *
 * Data sources:
 *   - All time return  -> profile.performance        (live)
 *   - All time P&L      -> profile.wallet_coin - STARTING_CAPITAL (live, derived from portfolio value)
 *   - Max drawdown      -> profile.max_drawdown       (live)
 *   - Win rate          -> profile.win_rate           (live)
 *   - Profit factor     -> profile.profit_factor      (live)
 *   - Current equity    -> profile.wallet_coin        (live)
 *   - Graph             -> last 1 month of performance history (live, same source
 *                          as the /performance/:userId chart via getUserPerformance)
 *   - Avg hold time     -> profile.avg_hold_seconds   (live)
 */

// Standard regular-portfolio starting balance (see calculatePerformanceByUser).
const STARTING_CAPITAL = 1000000;
// A league does NOT start at the sitewide figure. Every member is staked the same
// 100K when the league begins (the client calls it "league record dashboard 100K"),
// so P&L inside a league has to be measured from there.
const LEAGUE_STARTING_CAPITAL = 100000;

// --- static placeholders (to be wired to live data later) ---
// Average hold time (seconds from the API) -> compact "2d 6h" / "5h 30m" / "2m".
const formatHoldTime = (seconds) => {
  if (seconds == null) return "—";
  const s = Number(seconds);
  if (!Number.isFinite(s) || s < 0) return "—";
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${Math.floor(s)}s`;
};

// Whole dollars only (no cents) on the profile "Record" card so figures like
// +$392,143 never wrap. The portfolio page keeps its own (cents) formatter.
const formatCurrency = (n) => {
  const v = Number(n) || 0;
  return v.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

// Gain green / loss red — but exactly 0 is neutral grey with no sign (locked
// sitewide: a flat 0.00% / $0 is neither a gain nor a loss).
const greenRed = (v) => {
  const n = Number(v);
  if (n > 0) return "text-[#35B23C]";
  if (n < 0) return "text-[#FF4B4B]";
  return "text-secondary";
};

const Stat = ({ label, value, valueClass = "text-[#1A1A1A]" }) => (
  <div className="flex flex-col border border-[#F3F4F6] p-3">
    <span className="text-[12px] text-secondary font-inter">{label}</span>
    <span className={`text-[18px] font-bold font-poppins mt-1 break-words ${valueClass}`}>
      {value}
    </span>
  </div>
);

// All-time (full record) range — matches the all-time stats on "The Record"
// card. The Performance chart screen offers narrower ranges via its "All / 1Y /
// ..." filter; the sparkline always shows the full record.
const allTimeRange = () => ({ start: new Date(0), end: new Date() });

// Live performance sparkline: last 1 month of the user's performance history,
// same source as the /performance/:userId chart (getUserPerformance).
// Fetch the all-time performance points once, in the parent, so it can pick the
// layout: a populated sparkline sits beside the equity number, but the empty
// state needs the FULL card width (it clips in the narrow beside-equity column
// on desktop's ~360px right rail). See RecordEmptyState / EquityGraph below.
const useEquityPoints = (userId, gameId = null) => {
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!userId) {
        setPoints([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const { start, end } = allTimeRange();
        // Item 11: the curve ON the League Record card has to be the league's
        // record, not the member's sitewide one sitting under a "Verified
        // league performance" strapline.
        const res = await getUserPerformance(userId, start, end, gameId);
        const rows = Array.isArray(res?.data) ? res.data : [];
        const data = rows
          .map((item) => ({
            date: String(item.performance_updated_at).split(" ")[0],
            // Keep 2-decimal precision. Rounding to whole numbers turned small
            // real returns (e.g. 0.40%) into 0, which read as an empty curve.
            performance: Math.round(parseFloat(item.performance) * 100) / 100,
          }))
          .filter((d) => Number.isFinite(d.performance))
          .sort((a, b) => new Date(a.date) - new Date(b.date));
        if (!cancelled) setPoints(data);
      } catch (err) {
        console.error("profile performance graph fetch failed", err);
        if (!cancelled) setPoints([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [userId, gameId]);

  return { points, loading };
};

// A brand-new account has flat-0% history rows until the first trade, so "no
// points" OR "no movement off 0" both mean the curve hasn't started.
const pointsAreEmpty = (points) =>
  !points.length || !points.some((p) => Number(p.performance) !== 0);

const useVisibleReady = (ref, extraDeps = []) => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const check = () => setReady(!!ref.current && ref.current.offsetParent !== null);
    const raf = requestAnimationFrame(check);
    window.addEventListener("resize", check);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", check);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, ...extraDeps]);
  return ready;
};

// Decorative curve for the empty record: a two-state rising line (Recharts →
// true round start dot + green gradient fill), "building" vs "no trades yet".
const RecordEmptyCurve = ({ hasTraded = false }) => {
  const [fillId] = useState(() => `recEmptyFill-${curveIdCounter++}`);
  const curveData = emptyCurveData(hasTraded);
  const wrapRef = useRef(null);
  const ready = useVisibleReady(wrapRef);
  return (
    <div ref={wrapRef} className="w-full h-12 sm:h-14">
      {ready && (
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={curveData}
          margin={{ top: 6, right: 8, left: 8, bottom: 12 }}
        >
          <defs>
            <linearGradient id={fillId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#35b23c" stopOpacity={0.18} />
              <stop offset="100%" stopColor="#35b23c" stopOpacity={0} />
            </linearGradient>
          </defs>
          {/* Inset the data from the plot edges so the start dot isn't
              half-clipped by the plot-area clip (it sits on the first point). */}
          <XAxis hide dataKey="x" padding={{ left: 12, right: 6 }} />
          <YAxis hide domain={[0, "dataMax"]} />
          <Area
            type="monotone"
            dataKey="v"
            stroke="#35b23c"
            strokeWidth={2.5}
            fill={`url(#${fillId})`}
            dot={<StartDot />}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
      )}
    </div>
  );
};

// Empty-state copy: green title + grey body. Two states (building / no trades)
// and private vs public wording. Smaller on mobile (fits the side-by-side
// column); larger with more padding on desktop, where it spans the full card.
const RecordEmptyCopy = ({ isSelf = false, hasTraded = false }) => {
  const copy = performanceEmptyCopy({
    surface: "record",
    building: hasTraded,
    isSelf,
  });
  return (
    <div className="text-center px-1 mt-1">
      <p className="text-[13px] font-semibold text-[#35b23c] leading-snug">
        {copy.title}
      </p>
      {copy.body && (
        <p className="text-[13px] text-secondary mt-0.5 leading-snug">
          {copy.body}
        </p>
      )}
    </div>
  );
};

// Combined empty state (curve + copy stacked) — used where the card already
// gives the graph full width (the public/full-width layout).
const RecordEmptyState = ({ isSelf = false, hasTraded = false }) => (
  <div className="w-full my-2">
    <RecordEmptyCurve hasTraded={hasTraded} />
    <RecordEmptyCopy isSelf={isSelf} hasTraded={hasTraded} />
  </div>
);

// Sparkline for a populated record. Presentational — the parent fetches the
// points (useEquityPoints) and only renders this when there's real movement.
const EquityGraph = ({ points = [], loading = false }) => {
  const wrapRef = useRef(null);
  const ready = useVisibleReady(wrapRef, [loading]);

  if (loading) {
    // Grey rectangle at the sparkline's final height. It used to be an empty
    // div, which read as "this trader has no curve" rather than "loading".
    return (
      <div className="w-full sm:h-24 h-16 my-2">
        <SkeletonBlock width="100%" height="100%" rounded="rounded" />
      </div>
    );
  }

  // Color follows the sign of the current (latest) return — matching the
  // Performance chart line and the stat numbers (green up / red down).
  const lastVal = points[points.length - 1]?.performance ?? 0;
  const color = lastVal >= 0 ? "#35b23c" : "#ff4b4b";

  // Fill is the line's shadow: strongest AT THE LINE, fading to transparent at the
  // 0% baseline, on both sides of zero. Anchor the fill to 0 (baseValue + a domain
  // that includes 0) and put a transparent stop where 0 sits in the fill's box, so
  // below-zero segments flip the gradient instead of pooling color at the top.
  const vals = points.map((p) => p.performance);
  const fillTop = vals.length ? Math.max(...vals, 0) : 0;
  const fillBottom = vals.length ? Math.min(...vals, 0) : 0;
  const zeroOffsetPct =
    fillTop === fillBottom ? "0%" : `${(fillTop / (fillTop - fillBottom)) * 100}%`;
  const fillTopOpacity = fillTop > 0 ? 0.18 : 0;
  const fillBottomOpacity = fillBottom < 0 ? 0.18 : 0;

  return (
    <div ref={wrapRef} className="w-full sm:h-24 h-16 my-2">
      {ready && (
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={points}
          margin={{ top: 5, right: 0, left: 0, bottom: 0 }}
        >
          <defs>
            <linearGradient id="equityFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={fillTopOpacity} />
              <stop offset={zeroOffsetPct} stopColor={color} stopOpacity={0} />
              <stop offset="100%" stopColor={color} stopOpacity={fillBottomOpacity} />
            </linearGradient>
          </defs>
          <YAxis hide domain={[fillBottom, fillTop]} />
          <Area
            type="monotone"
            dataKey="performance"
            stroke={color}
            strokeWidth={2.5}
            fill="url(#equityFill)"
            baseValue={0}
            dot={false}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
      )}
    </div>
  );
};

const ProfileRecordCard = ({
  profile = {},
  userId,
  chartRight = false,
  isSelf = false,
  inLeague = false,
  title,
  subtitle,
  gameId = null,
  guideId,
  // The league this record belongs to, when it belongs to one. Only supplied on
  // a League Play profile; the main QUSHi record has no league and is unchanged.
  //
  // Client, "League Profile Routing": "For the completed league profile screen,
  // use frozen final data and change Current Equity to Final Equity." and
  // "replace Updated daily with Finalized Sep 12, 2026."
  //
  // Both are honesty about what the number IS. Once a league is finalised the
  // backend stops recalculating it entirely (UserController::finalisedGamesQuery),
  // so "Current Equity" describes a figure that is no longer current and
  // "Updated daily" promises a refresh that will never come — which is the same
  // complaint the client opened this round with, one screen further on.
  game = null,
}) => {
  const leagueCompleted =
    !!gameId &&
    !!game &&
    (game.status === "completed" || getLeagueStatus(game)?.tone === "ended");
  const equityLabel = leagueCompleted ? "Final Equity" : "Current Equity";

  // Client: "Replace 'Updated daily' with 'Updated often' — this should be
  // changed on league, games, and main QUSHi site records." One string here and
  // one in ProfileSkeleton, which draws the same line while the card loads.
  //
  // A COMPLETED league still says "Finalized <date>" instead: that record is
  // frozen and is not updated at any cadence, often or otherwise.
  const freshnessLabel = leagueCompleted ? finalizedOnLabel(game) : "Updated often";

  // What the two headline figures are scoped TO, which is the word the client
  // asked for in front of each:
  //
  //   main QUSHi record   "All Time Return" / "All Time P&L"  — "Keep the All
  //                       Time labels unchanged on the main QUSHi Record."
  //   cash game           "Game Return"    / "Game P&L"
  //   public / private    "League Return"  / "League P&L"
  //
  // Derived from the record's own league rather than passed in, so every caller
  // gets it right and the three labels can never disagree with the card's title
  // ("Cash Game Record" / "League Record" / "The Record"), which Profile.jsx
  // already splits the same three ways off the same two values.
  const recordScope = !gameId
    ? "All Time"
    : leagueTypeKey(game) === "cash"
    ? "Game"
    : "League";
  const currentEquity = Number(profile?.wallet_coin) || 0;
  const startingCapital = inLeague ? LEAGUE_STARTING_CAPITAL : STARTING_CAPITAL;
  const allTimePnl = currentEquity - startingCapital;
  const performance = Number(profile?.performance) || 0;
  const maxDrawdown = profile?.max_drawdown;
  const winRate = profile?.win_rate;
  const profitFactor = profile?.profit_factor;
  const perfUserId = userId || profile?.id;
  const hasTraded = Number(profile?.trades_count) > 0;
  const { points: equityPoints, loading: equityLoading } =
    useEquityPoints(perfUserId, gameId);
  const graphEmpty = !equityLoading && pointsAreEmpty(equityPoints);
  const [showPerfModal, setShowPerfModal] = useState(false);
  const navigate = useNavigate();

  const handleSparklineClick = () => {
    if (typeof window !== "undefined" && window.innerWidth >= 1024 && perfUserId) {
      navigate(`/performance/${perfUserId}`, {
        state: { user_name: profile?.user_name, hasTraded, isSelf },
      });
    } else {
      setShowPerfModal(true);
    }
  };

  return (
    <div id={guideId} className="mb-4 rounded-xl border border-[#F3F4F6] bg-white overflow-hidden">
      <div className="p-5">
      {/* Header */}
      <div className="flex items-center gap-2">
        <h2 className="text-[20px] font-bold font-poppins text-[#1A1A1A]">
          {title || "The Record"}
        </h2>
        <img src="/check-badge-icon.svg" alt="verified" className="w-5 h-5" />
      </div>
      <p className="text-[13px] text-secondary mt-1 font-inter">
        {subtitle ||
          (inLeague
            ? "Verified league performance"
            : "Verified performance on QUSHi")}
      </p>

      {chartRight ? (
        <div className="flex items-start gap-4 mt-5">
          <div className="shrink-0">
            <p className="text-[12px] tracking-wide text-secondary font-inter uppercase">
              {equityLabel}
            </p>
            <p className="text-[28px] font-bold font-poppins text-[#1A1A1A] leading-tight">
              ${formatCurrency(currentEquity)}
            </p>
          </div>
          <button
            type="button"
            onClick={handleSparklineClick}
            title="View full performance"
            className="flex-1 min-w-0 cursor-pointer [&_*]:cursor-pointer"
          >
            {graphEmpty ? (
              <RecordEmptyState isSelf={isSelf} hasTraded={hasTraded} />
            ) : (
              <EquityGraph points={equityPoints} loading={equityLoading} />
            )}
          </button>
        </div>
      ) : (
        <>
          <p className="text-[13px] text-secondary mt-4 mb-1.5 font-inter uppercase">
            {equityLabel}
          </p>
          <p className="text-[30px] font-bold font-poppins text-[#1A1A1A] leading-tight">
            ${formatCurrency(currentEquity)}
          </p>

          <button
            type="button"
            onClick={handleSparklineClick}
            title="View full performance"
            className="block w-full cursor-pointer [&_*]:cursor-pointer"
          >
            {graphEmpty ? (
              <RecordEmptyState isSelf={isSelf} hasTraded={hasTraded} />
            ) : (
              <EquityGraph points={equityPoints} loading={equityLoading} />
            )}
          </button>
        </>
      )}
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-3 mt-2">
        <Stat
          label={`${recordScope} Return`}
          value={`${Number(performance).toFixed(2)}%`}
          valueClass={greenRed(performance)}
        />
        <Stat
          label={`${recordScope} P&L`}
          value={`${allTimePnl < 0 ? "-" : ""}$${formatCurrency(
            Math.abs(allTimePnl)
          )}`}
          valueClass={greenRed(allTimePnl)}
        />
        <Stat
          label="Max Drawdown"
          value={
            maxDrawdown != null
              ? `${Math.abs(Number(maxDrawdown)).toFixed(2)}%`
              : "—"
          }
        />
        <Stat
          label="Win Rate"
          value={
            winRate != null && profile?.avg_hold_seconds != null
              ? `${Math.round(Number(winRate))}%`
              : "—"
          }
        />
        {/* No profit factor -> a dash, like every other stat on this card.
            It used to print "∞" for a trader with wins and no losses, which
            was ours, not the client's, and it read as a glyph error rather
            than a number: "Check out the profit factor stat in the record on
            the dashboard screen. What is that symbol/icon? Not sure what that
            mark is. Normally, it's a dash if no stat is available." */}
        <Stat
          label="Profit Factor"
          value={profitFactor != null ? Number(profitFactor).toFixed(2) : "—"}
        />
        <Stat
          label="Avg Hold Time"
          value={formatHoldTime(profile?.avg_hold_seconds)}
        />
      </div>

      {/* Footer */}
      <div className="flex justify-between items-center mt-5 p-4 border-t border-[#F3F4F6] bg-[#F9FAFB]">
        <span className="flex items-center gap-1.5 text-[13px] text-secondary">
          <img src="/check-badge-icon.svg" alt="" className="w-4 h-4" />
          Verified by QUSHi
        </span>
        <span className="text-[13px] text-secondary">{freshnessLabel}</span>
      </div>

      {/* Full performance chart modal (opened by tapping the mini chart) */}
      {showPerfModal && (
        <div
          className="fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-center items-center p-4"
          onClick={() => setShowPerfModal(false)}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-3xl max-h-[88vh] overflow-auto relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowPerfModal(false)}
              aria-label="Close"
              className="absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center rounded-full text-gray-600 hover:bg-gray-100 bg-white"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 011.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
            <div className="p-4 pt-12">
              <PerformanceSection userId={perfUserId} embedded minHeight={false} isSelfProfile={isSelf} hasTraded={hasTraded} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileRecordCard;
